import { Spinner, Box, Heading, IconButton, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, useDisclosure, Image, HStack } from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { Routes, Route } from "react-router-dom";
import { Sidebar } from "../components/dashboard/Sidebar";
import { ConnectBank } from "../components/dashboard/Start";
import styled from "styled-components";
import { Analytics } from "../components/dashboard/Analytics";
import { Welcome } from "../components/dashboard/Welcome";
import { Contacts } from "../components/dashboard/Contacts";
import { Payments } from "../components/dashboard/Payments";
import { Payouts } from "../components/dashboard/Payouts";
import { Settings } from "../components/dashboard/Settings";
import { useAuth } from "../hooks/useAuth";
import { useViewport } from "../hooks/useViewport";
import { Show } from "../components/Show";
import { useEffect, useState, useRef } from "react";
import { SpinWrapper } from "../components/Reusable";
import { postCreateClient } from "../api/clientUser/postCreateClient";
import { postCreateUser } from "../api/user/postCreateUser";
import supabase from "../supabase";

const Container = styled.div`
  display: flex;
  background-color: #f8fafc;
  min-height: 100vh;
  overflow-x: hidden;
  max-width: 100vw;
  width: 100%;
  position: relative;
`;

const ContentContainer = styled.div<{ $isMobile: boolean }>`
  padding: ${props => props.$isMobile ? "1rem" : "2rem 3rem"};
  padding-top: ${props => props.$isMobile ? "5rem" : "2rem"};
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 100vh;
  background: linear-gradient(135deg, #e3f2fd 0%, white 50%, #f8fafc 100%);
`;

const MobileHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100vw;
  z-index: 100;
  background: white;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

export const Dashboard = () => {
  const { onboardingResults, getOnboardingResults, accessToken } = useAuth();
  const { isMobile } = useViewport();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingSignupHandled, setPendingSignupHandled] = useState(false);
  const handlingSignup = useRef(false);

  useEffect(() => {
    const handlePendingSignup = async () => {
      // Prevent duplicate runs
      if (handlingSignup.current || pendingSignupHandled) return;

      const pendingSignupData = localStorage.getItem("pendingSignup");
      if (pendingSignupData && accessToken) {
        handlingSignup.current = true;
        try {
          const { email, orgOrIndividualName, ein, accountType } = JSON.parse(pendingSignupData);
          const { data: { user } } = await supabase.auth.getUser();

          if (user) {
            if (accountType === "create-organization" || accountType === "create-individual") {
              await postCreateClient(user.id, email, orgOrIndividualName, ein, accessToken);
            } else {
              await postCreateUser(user.id, email, orgOrIndividualName, accessToken);
            }
            localStorage.removeItem("pendingSignup");
            setPendingSignupHandled(true);
            await getOnboardingResults(accessToken);
          }
        } catch (error) {
          console.error("Failed to complete signup:", error);
          // Remove pending signup data even on error to prevent infinite loop
          localStorage.removeItem("pendingSignup");
          setPendingSignupHandled(true);
        } finally {
          handlingSignup.current = false;
        }
      }
    };

    if (onboardingResults.organization) return;
    if (!accessToken) return;

    (async () => {
      await handlePendingSignup();
      // Only fetch onboarding if we don't have it and haven't just handled signup
      if (!onboardingResults.organization) {
        await getOnboardingResults(accessToken);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, onboardingResults.organization]);

  return (
    <Container>
      {!isMobile && <Sidebar />}

      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <Sidebar onClose={onClose} />
          </DrawerContent>
        </Drawer>
      )}

      <ContentContainer $isMobile={isMobile}>
        {isMobile && (
          <MobileHeader>
            <IconButton
              icon={<HamburgerIcon />}
              aria-label="Open menu"
              onClick={onOpen}
              variant="ghost"
              size="lg"
              position="absolute"
              left="1rem"
            />
            <HStack spacing={2}>
              {onboardingResults.logo && (
                <Image
                  src={onboardingResults.logo}
                  alt="Organization logo"
                  boxSize="32px"
                  borderRadius="4px"
                  objectFit="cover"
                />
              )}
              <Heading
                size="md"
                color="#1A202C"
                fontWeight="700"
                textAlign="center"
              >
                {onboardingResults.organization || "Dashboard"}
              </Heading>
            </HStack>
          </MobileHeader>
        )}

        <Show
          when={!!onboardingResults.organization}
          whenFalseContent={
            <SpinWrapper>
              <Spinner color="#339CFF" size="xl" />
            </SpinWrapper>
          }
        >
          {!isMobile && (
            <Box mb={6}>
              <HStack spacing={3}>
                {onboardingResults.logo && (
                  <Image
                    src={onboardingResults.logo}
                    alt="Organization logo"
                    boxSize="40px"
                    borderRadius="6px"
                    objectFit="cover"
                  />
                )}
                <Heading
                  size="lg"
                  color="#1A202C"
                  fontWeight="700"
                >
                  {onboardingResults.organization}
                </Heading>
              </HStack>
            </Box>
          )}
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="start" element={<ConnectBank />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="payments" element={<Payments />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="settings" element={<Settings />} />
          </Routes>
        </Show>
      </ContentContainer>
    </Container>
  );
};
